'use client';
import { CheckCircle, Download } from 'lucide-react';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { components } from '@/types/strapi';

type FormInput = components['schemas']['SharedFormComponent'];
interface FormComponentProps extends FormInput {
	submit_button?: components['schemas']['SharedLinkComponent'];
	pdf_url?: string;
	pdf_name?: string;
}

export const FormComponent: React.FC<FormComponentProps> = ({
	inputs,
	submit_button = { text: 'Submit', variant: 'default' },
	pdf_url = '',
	pdf_name = 'case study',
}: FormComponentProps) => {
	const [formData, setFormData] = useState<{ [key: string]: string }>({});
	const [errors, setErrors] = useState<{ [key: string]: string }>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setisSubmitted] = useState(false);
	const router = useRouter();

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		fallbackName: string,
	) => {
		const { name, value } = e.target;
		const key = name || fallbackName;
		setFormData(prev => ({ ...prev, [key]: value }));
		// Clear error when user starts typing
		if (errors[key]) {
			setErrors(prev => ({ ...prev, [key]: '' }));
		}
	};
	const handleDownload = (url: string, name: string) => {
		const link = document.createElement('a');
		link.href = url;
		link.download = name;
		link.target = '_blank';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const validateForm = () => {
		const newErrors: { [key: string]: string } = {};
		let isValid = true;

		inputs?.forEach((input, index) => {
			const inputName = input.name || `unnamed_${index}`;
			const value = formData[inputName]?.trim() || '';

			if (!value) {
				newErrors[inputName] =
					`${input.name || 'This field'} is required`;
				isValid = false;
			}
		});

		setErrors(newErrors);
		return isValid;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);

		// Prepare payload with metadata
		const payload = {
			submittedValues: {
				fields: inputs?.map((input, index) => ({
					name: input.name || `unnamed_${index}`,
					value: formData[input.name || `unnamed_${index}`] || '',
				})),
			},
			pageURL: window.location.href,
			submittedBy: 'Anonymous',
			submittedAt: new Date().toISOString(),
			userAgent: navigator.userAgent,
		};

		try {
			const response = await fetch(
				process.env.NEXT_PUBLIC_API_URL + '/api/form-submissions',
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ data: payload }),
				},
			);

			if (!response.ok) throw new Error('Failed to submit form');
			await response.json();
			setisSubmitted(true);
			setFormData({});
			setErrors({});
		} catch (error) {
			console.error('Error:', error);
			setErrors({ submit: 'Failed to submit form. Please try again.' });
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="flex flex-col gap-4">
			{isSubmitted ? (
				<div className="flex flex-col items-center justify-center space-y-4 rounded-lg bg-blue-50 px-6 py-8 text-center">
					<CheckCircle className="h-12 w-12 text-blue-500" />
					<h3 className="text-xl font-semibold text-blue-600">
						Thank you for your submission!
					</h3>
					{pdf_url && pdf_url != '' && (
						<Button
							variant="primary"
							className="mt-2"
							onClick={() => {
								handleDownload(pdf_url, pdf_name);
							}}>
							<Download className="mr-2 h-5 w-5" />
							Download
						</Button>
					)}
				</div>
			) : (
				<div className="space-y-4">
					<form
						id="form-component"
						className="space-y-6"
						onSubmit={handleSubmit}>
						{inputs?.map((input, index) => {
							const inputName = input.name || `unnamed_${index}`;
							return (
								<div key={inputName} className="space-y-2">
									<label
										htmlFor={inputName}
										className="block text-sm font-medium leading-6">
										{input.name || 'Unnamed Field'}{' '}
									</label>
									<div className="mt-1">
										{input.type === 'textarea' ? (
											<textarea
												rows={5}
												id={inputName}
												name={inputName}
												placeholder={input.placeholder}
												value={
													formData[inputName] || ''
												}
												onChange={e =>
													handleChange(e, inputName)
												}
												className={`border-border-color placeholder:text-placeholder block w-full rounded-md border bg-background px-4 py-2 text-black text-primary focus:outline-none focus:ring-2 focus:ring-accent sm:text-sm sm:leading-6 ${
													errors[inputName]
														? 'border-red-500'
														: ''
												}`}
												aria-label={
													input.name ||
													'Unnamed Field'
												}
											/>
										) : (
											<input
												id={inputName}
												name={inputName}
												type={input.type}
												placeholder={input.placeholder}
												value={
													formData[inputName] || ''
												}
												onChange={e =>
													handleChange(e, inputName)
												}
												className={`border-border-color placeholder:text-placeholder block w-full rounded-md border bg-background px-4 py-2 text-black text-primary focus:outline-none focus:ring-2 focus:ring-accent sm:text-sm sm:leading-6 ${
													errors[inputName]
														? 'border-red-500'
														: ''
												}`}
												aria-label={
													input.name ||
													'Unnamed Field'
												}
											/>
										)}
										{errors[inputName] && (
											<p className="mt-1 text-sm text-red-500">
												{errors[inputName]}
											</p>
										)}
									</div>
								</div>
							);
						})}
						{errors.submit && (
							<p className="text-sm text-red-500">
								{errors.submit}
							</p>
						)}
					</form>
					{submit_button.URL ? (
						<Button
							variant={submit_button.variant}
							className="w-full rounded-md py-2 font-semibold text-white transition duration-300"
							onClick={() => router.push(`${submit_button.URL}`)}>
							{submit_button.text || 'Submit'}
						</Button>
					) : (
						<Button
							type="submit"
							form="form-component"
							variant={submit_button.variant}
							disabled={isSubmitting}
							className="w-full rounded-md py-2 font-semibold text-white transition duration-300">
							{isSubmitting
								? 'Submitting...'
								: submit_button.text || 'Submit'}
						</Button>
					)}
				</div>
			)}
		</div>
	);
};
